<script setup lang="ts">
    import { getConfig, getErrorMessage, klinikenApi } from "@/api";
    import KFormFieldDisplay from "@/components/forms/displays/FormFieldDisplay/KFormFieldDisplay.vue";
    import KForm from "@/components/forms/Form/KForm.vue";
    import KGroup from "@/components/forms/groups/Group/KGroup.vue";
    import { Option } from "@/components/forms/inputs/Autocomplete/types";
    import ProfileAutocomplete from "@/components/forms/reusables/ProfileAutocomplete.vue";
    import { convertProfileData } from "@/components/forms/reusables/profileHelper";
    import ModalContent from "@/components/generic/ModalContent.vue";
    import { ProfileDetail } from "@/types";
    import { openDialog } from "@/utils";
    import moment from "moment";
    import { ref, toRefs } from "vue";

    type Personal = {
        profile: string | { value: string; label: string } | null;
        profile_display?: ProfileDetail;
        fran: any;
        till: any;
    };

    type Handelse = {
        namn: string;
        fran: any;
        till: any;
    };

    const props = defineProps<{
        salsbemanning: {
            id: number | null;
            sal: number;
            sal_display: {
                id: number;
                namn: string;
                ordning: number;
                org_units: any[];
                orgunits_display: any[];
            };
            datum: string;
            personal: Personal[];
            handelser: Handelse[];
            kommentar: string | null;
        };
    }>();

    const { salsbemanning } = toRefs(props);

    // TODO: Ska bli inställning vilka roller som ska gå att välja emellan
    const availableRoles = ref([
        {
            code: "kirurg",
            displayName: "Kirurg",
        },
        {
            code: "operationssjukskoterska",
            displayName: "Operationssjuksköterska",
        },
        {
            code: "operationsunderskoterska",
            displayName: "Operationsundersköterska",
        },
        {
            code: "anestesilakare",
            displayName: "Anestesiläkare",
        },
        {
            code: "anestesisjukskoterska",
            displayName: "Anestesisjuksköterska",
        },
    ]);

    const initForm = () => {
        const formData: any = {
            sal: salsbemanning.value.sal,
            datum: salsbemanning.value.datum,
            kommentar: salsbemanning.value.kommentar,
        };

        // Skapar en map av roller till personal
        const personalByRole: Record<string, Personal[]> = {};
        availableRoles.value.forEach((roll) => {
            personalByRole[roll.code] = [];
        });
        salsbemanning.value.personal.forEach((personal) => {
            const roll_code = personal.profile_display?.roll_display?.code;
            if (roll_code && Object.keys(personalByRole).includes(roll_code)) {
                personalByRole[roll_code].push({
                    profile: convertProfileData({ profile: personal.profile_display as ProfileDetail }),
                    fran: moment(personal.fran).format("HH:mm"),
                    till: moment(personal.till).format("HH:mm"),
                });
            }
        });

        // Om det saknas personal för en roll, lägg till ett default objekt.
        availableRoles.value.forEach((roll) => {
            if (personalByRole[roll.code].length === 0) {
                personalByRole[roll.code].push({
                    profile: "",
                    fran: "07:00",
                    till: "17:00",
                });
            }
            formData[roll.code] = personalByRole[roll.code];
        });

        formData.handelser = salsbemanning.value.handelser.map((handelse) => {
            return {
                namn: handelse.namn,
                fran: moment(handelse.fran).format("HH:mm"),
                till: moment(handelse.till).format("HH:mm"),
            };
        });

        return formData;
    };

    const emit = defineEmits(["submitted", "close"]);

    const close = () => {
        emit("close");
    };

    const repeaterConverter = ({
        value,
    }: {
        value: { profile: string | Option; [key: string]: unknown; fran: any; till: any }[];
    }) => {
        return value.map(({ profile, fran, till }): unknown => {
            if (typeof profile === "object" && profile !== null) {
                return {
                    profile: (profile as Option).value,
                    fran,
                    till,
                };
            }

            return {
                profile,
                fran,
                till,
            };
        });
    };

    const submit = async (data: any) => {
        let personal: Personal[] = [];

        // Slår ihop personalgrupperna till en lista
        availableRoles.value.forEach((roll) => {
            personal = [...personal, ...data[roll.code]];
        });

        // Rensar eventuell personal utan vald profil och sätter rätt datum på klockslagen
        personal = personal
            .filter((p: Personal) => !!p.profile)
            .map((p: Personal) => ({
                profile: p.profile,
                fran: moment(`${data.datum} ${p.fran}`),
                till: moment(`${data.datum} ${p.till}`),
            }));
        // Rensar eventuella händelser med blanka namn och sätter rätt datum på klockslagen
        const handelser = data.handelser
            .filter((h: Handelse) => !!h.namn)
            .map((h: Handelse) => ({
                namn: h.namn,
                fran: moment(`${data.datum} ${h.fran}`),
                till: moment(`${data.datum} ${h.till}`),
            }));

        const payload = {
            sal: data.sal,
            datum: data.datum,
            personal: personal,
            handelser: handelser,
            kommentar: data.kommentar,
        };

        const url = "/operation/salsbemanning/";

        try {
            let response = null;

            if (salsbemanning.value.id) {
                response = await klinikenApi.put(`${url}${salsbemanning.value.id}/`, payload, getConfig({}));
            } else {
                response = await klinikenApi.post(`${url}`, payload, getConfig({}));
            }

            emit("submitted", response.data);
        } catch (e) {
            openDialog(getErrorMessage(e), "error");
        }
    };
</script>

<template>
    <ModalContent title="Personal och tider" :showCloseButton="true" @close="close" width="wide">
        <KForm
            id="personal-och-tider-form"
            class="personal-och-tider"
            @submit="submit"
            @cancel="close"
            submitContent="Spara"
            cancelContent="Avbryt"
            :initialValuesGetter="initForm"
            variant="dialog"
            columns="12"
        >
            <div class="form-content">
                <KFormFieldDisplay name="sal" label="Opsal" :value="salsbemanning.sal_display.namn" class="span-five" />
                <KFormFieldDisplay name="datum" label="Datum" :value="salsbemanning.datum" class="span-seven" />
                <template>
                    <KGroup name="personal" label="Personal" class="span-all" margin="default">
                        <KRepeater
                            v-for="roll in availableRoles"
                            :key="roll.code"
                            :name="roll.code"
                            #default="{ index, model }"
                            class="span-all"
                            :converter="repeaterConverter"
                        >
                            <ProfileAutocomplete
                                name="profile"
                                :label="roll.displayName"
                                :value="model?.[index]?.profile ?? null"
                                :urlParams="{
                                    roll__code: roll.code,
                                }"
                                class="span-five"
                            />
                            <FormulateInput type="timepicker" name="fran" label="Från" class="span-three" />
                            <FormulateInput type="timepicker" name="till" label="Till" class="span-three" />
                        </KRepeater>
                    </KGroup>
                </template>
                <KGroup name="tider" label="Tider" class="span-all" margin="default">
                    <KRepeater name="handelser" #default="{ index, model }" class="span-all">
                        <KText
                            name="namn"
                            label="Händelse"
                            :value="model?.[index]?.namn ?? null"
                            validation="max:40"
                            class="span-five"
                        />
                        <FormulateInput type="timepicker" name="fran" label="Från" class="span-three" />
                        <FormulateInput type="timepicker" name="till" label="Till" class="span-three" />
                    </KRepeater>
                </KGroup>
                <KGroup name="kommentar" label="Kommentar" class="span-all" margin="default">
                    <KText name="kommentar" label="Kommentar" class="span-eleven" validation="max:65" :maxCount="65" />
                </KGroup>
            </div>
        </KForm>
    </ModalContent>
</template>
